/**
 * @fileoverview Componente responsável por exibir o histórico de atividades do CRM.
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Drawer,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Tooltip,
  makeStyles,
} from "@material-ui/core";
import api from "../../services/api";

import {
  Pencil,
  X,
  Trash2,
  PlusCircle,
  ArrowRightLeft,
  UserPlus,
  Columns3,
  Activity,
} from "lucide-react";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    drawer: {
      "& .MuiDrawer-paper": {
        width: 420,
        maxWidth: "92vw",
        background: dark ? "#0A0A0A" : "#F8FAFC",
        borderLeft: dark
          ? "1px solid rgba(255,255,255,0.06)"
          : "1px solid rgba(15,23,42,0.06)",
        boxShadow: dark
          ? "-24px 0 64px -16px rgba(0,0,0,0.6)"
          : "-24px 0 64px -16px rgba(15,23,42,0.18)",
        display: "flex",
        flexDirection: "column",
      },
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: theme.spacing(2.5, 3),
      borderBottom: dark
        ? "1px solid rgba(255,255,255,0.05)"
        : "1px solid rgba(15,23,42,0.06)",
      flexShrink: 0,
    },
    headerLeft: { display: "flex", flexDirection: "column", gap: 2 },
    title: {
      fontSize: "1.18rem",
      fontWeight: 700,
      letterSpacing: "-0.01em",
      color: dark ? "#E8EAED" : "#0F172A",
    },
    subtitle: {
      fontSize: 12.5,
      color: dark ? "#9CA3AF" : "#64748B",
    },
    closeBtn: {
      width: 34,
      height: 34,
      borderRadius: 10,
      color: dark ? "#9CA3AF" : "#475569",
      background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.07)" : "rgba(15,23,42,0.08)",
      },
    },

    body: {
      flex: 1,
      overflowY: "auto",
      padding: theme.spacing(2, 2.5),
      "&::-webkit-scrollbar": { width: 6 },
      "&::-webkit-scrollbar-thumb": {
        background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.08)",
        borderRadius: 3,
      },
    },

    // Timeline
    timeline: {
      position: "relative",
      paddingLeft: 26,
    },
    timelineLine: {
      position: "absolute",
      left: 16,
      top: 8,
      bottom: 8,
      width: 1,
      background: dark
        ? "linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.03) 100%)"
        : "linear-gradient(180deg, rgba(15,23,42,0.10) 0%, rgba(15,23,42,0.03) 100%)",
    },

    item: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      gap: 2,
      padding: "10px 12px",
      borderRadius: 10,
      marginBottom: 6,
      transition: "background .12s",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.025)" : "rgba(15,23,42,0.025)",
      },
    },
    itemIcon: {
      position: "absolute",
      left: -26,
      top: 8,
      width: 24,
      height: 24,
      borderRadius: "50%",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      border: dark
        ? "2px solid #0A0A0A"
        : "2px solid #F8FAFC",
      boxShadow: dark
        ? "0 0 0 1px rgba(255,255,255,0.04)"
        : "0 0 0 1px rgba(15,23,42,0.06)",
    },
    description: {
      fontSize: 13,
      lineHeight: 1.45,
      color: dark ? "#D1D5DB" : "#0F172A",
    },
    bold: {
      fontWeight: 600,
      color: dark ? "#E8EAED" : "#0F172A",
    },
    quoted: {
      fontWeight: 500,
      color: dark ? "#E8EAED" : "#0F172A",
    },
    timestamp: {
      fontSize: 11,
      color: dark ? "#6B7280" : "#94A3B8",
      marginTop: 2,
      fontVariantNumeric: "tabular-nums",
    },

    loadMore: {
      display: "flex",
      justifyContent: "center",
      padding: 16,
    },
    loadMoreBtn: {
      textTransform: "none",
      borderRadius: 10,
      fontSize: 13,
      fontWeight: 600,
      padding: "8px 16px",
      color: dark ? "#A5B4FC" : "#3D5CFD",
      "&:hover": {
        background: dark ? "rgba(99,102,241,0.06)" : "rgba(61,92,253,0.06)",
      },
    },
    loadingWrap: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: 24,
      color: dark ? "#9CA3AF" : "#64748B",
    },

    empty: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      padding: "60px 24px",
      textAlign: "center",
      color: dark ? "#9CA3AF" : "#64748B",
    },
    emptyIconWrap: {
      width: 56,
      height: 56,
      borderRadius: 14,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
      color: dark ? "#9CA3AF" : "#94A3B8",
      marginBottom: 4,
    },
    emptyTitle: {
      fontSize: 14,
      fontWeight: 600,
      color: dark ? "#E8EAED" : "#0F172A",
    },
    emptyHint: {
      fontSize: 12.5,
      maxWidth: 260,
      lineHeight: 1.5,
    },
  };
});

const ACTION_CONFIG = {
  card_created:    { Icon: PlusCircle,     color: "#22c55e", verb: "criou card" },
  card_updated:    { Icon: Pencil,         color: "#f59e0b", verb: "editou card" },
  card_deleted:    { Icon: Trash2,         color: "#ef4444", verb: "excluiu card" },
  card_assigned:   { Icon: UserPlus,       color: "#3b82f6", verb: "atribuiu card" },
  card_moved:      { Icon: ArrowRightLeft, color: "#8b5cf6", verb: "moveu card" },
  column_created:  { Icon: Columns3,       color: "#22c55e", verb: "criou coluna" },
  column_updated:  { Icon: Pencil,         color: "#f59e0b", verb: "editou coluna" },
  column_deleted:  { Icon: Trash2,         color: "#ef4444", verb: "excluiu coluna" },
};

const formatTimeAgo = (dateStr) => {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHour = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return "agora";
  if (diffMin < 60) return `${diffMin}min atrás`;
  if (diffHour < 24) return `${diffHour}h atrás`;
  if (diffDay < 7) return `${diffDay}d atrás`;
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
};

const CrmActivityLogDrawer = ({ open, onClose }) => {
  const classes = useStyles();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);

  const fetchLogs = useCallback(async (newOffset = 0) => {
    setLoading(true);
    try {
      const { data } = await api.get("/crm-activity-logs", {
        params: { limit: 50, offset: newOffset },
      });
      if (newOffset === 0) {
        setLogs(data.logs);
      } else {
        setLogs((prev) => [...prev, ...data.logs]);
      }
      setHasMore(data.hasMore);
      setOffset(newOffset + data.logs.length);
    } catch (err) {
      console.error("Failed to fetch CRM activity logs:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) {
      setOffset(0);
      fetchLogs(0);
    }
  }, [open, fetchLogs]);

  const renderItem = (log) => {
    const config = ACTION_CONFIG[log.action] || ACTION_CONFIG.card_updated;
    const Icon = config.Icon;
    const details = log.details || {};

    let body;
    if (log.action === "card_moved" && details.fromColumn && details.toColumn) {
      body = (
        <>
          <span className={classes.bold}>{log.userName}</span> moveu{" "}
          <span className={classes.quoted}>"{log.entityName}"</span> de{" "}
          <span className={classes.quoted}>"{details.fromColumn}"</span> para{" "}
          <span className={classes.quoted}>"{details.toColumn}"</span>
        </>
      );
    } else if (log.action === "card_assigned" && details.assignedToUserName) {
      body = (
        <>
          <span className={classes.bold}>{log.userName}</span> atribuiu{" "}
          <span className={classes.quoted}>"{log.entityName}"</span> a{" "}
          <span className={classes.quoted}>{details.assignedToUserName}</span>
        </>
      );
    } else {
      body = (
        <>
          <span className={classes.bold}>{log.userName}</span> {config.verb}{" "}
          <span className={classes.quoted}>"{log.entityName}"</span>
        </>
      );
    }

    return (
      <div key={log.id} className={classes.item}>
        <div
          className={classes.itemIcon}
          style={{
            background: `${config.color}1F`,
            color: config.color,
          }}
        >
          <Icon size={12} strokeWidth={2} />
        </div>
        <Typography className={classes.description}>{body}</Typography>
        <Tooltip title={new Date(log.createdAt).toLocaleString("pt-BR")} placement="left">
          <span className={classes.timestamp}>{formatTimeAgo(log.createdAt)}</span>
        </Tooltip>
      </div>
    );
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} className={classes.drawer}>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <Typography className={classes.title}>Atividades Recentes</Typography>
          <Typography className={classes.subtitle}>
            Histórico de ações no CRM
          </Typography>
        </div>
        <Tooltip title="Fechar">
          <IconButton className={classes.closeBtn} onClick={onClose}>
            <X size={18} strokeWidth={1.75} />
          </IconButton>
        </Tooltip>
      </div>

      <div className={classes.body}>
        {logs.length === 0 && !loading ? (
          <div className={classes.empty}>
            <div className={classes.emptyIconWrap}>
              <Activity size={22} strokeWidth={1.5} />
            </div>
            <Typography className={classes.emptyTitle}>
              Nenhuma atividade ainda
            </Typography>
            <Typography className={classes.emptyHint}>
              Toda ação no quadro — criação, edição, movimentação — aparecerá aqui.
            </Typography>
          </div>
        ) : (
          <div className={classes.timeline}>
            <div className={classes.timelineLine} />
            {logs.map(renderItem)}
          </div>
        )}

        {loading && (
          <div className={classes.loadingWrap}>
            <CircularProgress size={22} />
          </div>
        )}

        {hasMore && !loading && (
          <div className={classes.loadMore}>
            <Button
              className={classes.loadMoreBtn}
              size="small"
              onClick={() => fetchLogs(offset)}
            >
              Carregar mais
            </Button>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default CrmActivityLogDrawer;
